import { useMemo, useState } from 'react';
import { FileText, Pencil, Plus, Sheet, Trash2, X } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { EntitySelect } from '@/components/EntitySelect';
import { PersonAvatar } from '@/components/PersonAvatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { downloadFile, exportCsv } from '@/core/api';
import { useUrlState } from '@/hooks/useUrlState';
import { useSession } from '@/stores/session';
import { MemberSelect } from '@/features/members';
import { TimeEntryFormDialog } from '../components/TimeEntryFormDialog';
import {
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useUpdateTimeEntry,
  useWorklogs,
  useWorklogSummary,
} from '../queries';
import type { WorklogGroupBy, WorklogSummaryGroup } from '../api';
import { resolveProjectLabel, searchProjects } from '../select';
import { formatMinutes } from '../timeFormat';
import type { TimeEntry, TimeEntryInput } from '../types';

const WORKLOGS_PATH = '/projects/worklogs/';

/** "Group by" choices — subtotals come from `worklogs/summary/?group_by=` over
 *  the full filtered set (DEV13-61), never from the visible page. */
const GROUP_BY_OPTIONS: { value: WorklogGroupBy | 'none'; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'user', label: 'User' },
  { value: 'project', label: 'Project' },
  { value: 'work_item', label: 'Work item' },
  { value: 'date', label: 'Date' },
];
const GROUP_BY_VALUES = new Set<string>(['user', 'project', 'work_item', 'date']);

/** Cross-project timesheets — "Download worklogs AKA timesheets for anyone in
 * any project" (ADR-071). Time can also be logged manually here (the timer on a
 * work item is the other way in — ADR-076). Others' rows are masked server-side
 * for non-managing roles (C7); managers see everyone's. */
export function ProjectWorklogsPage() {
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Set by clicking a work-item group (there is no work-item picker here).
  const [workItem, setWorkItem] = useState<{ id: number; label: string } | null>(null);
  const [page, setPage] = useState(1);
  // Group by lives in the URL so a shared link keeps the view (FE-ADR-056).
  const [rawGroupBy, setRawGroupBy] = useUrlState('group_by', 'none');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | undefined>(undefined);
  const [toDelete, setToDelete] = useState<TimeEntry | null>(null);

  const role = useSession((s) => s.role);
  const activeSlug = useSession((s) => s.activeOrgSlug);
  const org = useSession((s) => s.organizations.find((o) => o.slug === activeSlug));
  // Time managers (Founder/Super Admin/Accountant) bypass the org manual switch
  // and may view anyone's worklogs; only Founder/Super Admin log on behalf of
  // others (ADR-077).
  const isTimeManager = role === 'super_admin' || role === 'founder' || role === 'accountant';
  const canLogForOthers = role === 'super_admin' || role === 'founder';
  const manualEnabled = (org?.manual_time_logging_enabled ?? true) || isTimeManager;
  // Non-managers only ever see their own time, so "By user" would be one row —
  // offered to time managers only, and a shared `?group_by=user` reads as none.
  const groupByOptions = isTimeManager
    ? GROUP_BY_OPTIONS
    : GROUP_BY_OPTIONS.filter((o) => o.value !== 'user');
  const groupBy =
    GROUP_BY_VALUES.has(rawGroupBy) && (rawGroupBy !== 'user' || isTimeManager)
      ? (rawGroupBy as WorklogGroupBy)
      : null;

  // Filters only (no page) — exports and the summary cover the FULL filtered set.
  const filterParams = {
    user: userId ? Number(userId) : undefined,
    project: projectId ? Number(projectId) : undefined,
    work_item: workItem?.id,
    date_from: from || undefined,
    date_to: to || undefined,
  };
  const { data, isPending, isError, error } = useWorklogs({
    ...filterParams,
    ordering: '-date',
    page,
  });
  const rows = useMemo(() => data?.results ?? [], [data]);
  // The list is paginated — the total comes from the server-side aggregate,
  // never from summing the visible page (that undercounts past one page).
  const { data: summary } = useWorklogSummary({ ...filterParams, group_by: groupBy ?? undefined });
  // While a new dimension loads, the previous summary stays on screen
  // (keepPreviousData) — only show/act on groups the server says match.
  const groups = groupBy && summary?.group_by === groupBy ? summary.groups : [];

  /** Narrow the table to one group (users only for time managers — others
   *  only ever see their own time). Groups without a key aren't filterable. */
  function canFilterBy(group: WorklogSummaryGroup) {
    return group.key !== null && summary?.group_by === groupBy;
  }

  function filterToGroup(group: WorklogSummaryGroup) {
    if (!canFilterBy(group)) return;
    if (groupBy === 'user') setUserId(String(group.key));
    else if (groupBy === 'project') setProjectId(String(group.key));
    else if (groupBy === 'work_item') setWorkItem({ id: Number(group.key), label: group.label });
    else if (groupBy === 'date') {
      setFrom(String(group.key));
      setTo(String(group.key));
    }
    setPage(1);
  }

  const create = useCreateTimeEntry();
  const update = useUpdateTimeEntry();
  const remove = useDeleteTimeEntry();

  async function handleSubmit(input: TimeEntryInput) {
    // Manual logging off: only the note is editable — send just that (the API
    // refuses any change to the time itself).
    if (editing) {
      await update.mutateAsync({
        id: editing.id,
        input: manualEnabled ? input : { description: input.description },
      });
    } else await create.mutateAsync(input);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Worklogs</h1>
          <p className="text-muted-foreground text-sm">
            {manualEnabled
              ? 'Log time and download worklogs (timesheets) for anyone, in any project.'
              : 'Download worklogs (timesheets) for anyone, in any project.'}
          </p>
        </div>
        {manualEnabled ? (
          <Button
            onClick={() => {
              setEditing(undefined);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" /> Log time
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          {/* Time managers (Founder/Super Admin/Accountant) view anyone's timesheet; others see their own (ADR-077). */}
          {isTimeManager ? (
            <div className="w-48">
              <MemberSelect
                value={userId}
                onChange={(v) => {
                  setUserId(v);
                  setPage(1);
                }}
                clearable
                placeholder="All users"
              />
            </div>
          ) : null}
          <div className="w-56">
            <EntitySelect
              queryKey="worklog-project-filter"
              value={projectId}
              onChange={(v) => {
                setProjectId(v);
                setWorkItem(null); // a work item belongs to one project
                setPage(1);
              }}
              search={searchProjects}
              resolveLabel={resolveProjectLabel}
              placeholder="All projects"
            />
          </div>
          <DateRangeFilter
            label="Date"
            idPrefix="worklog-date"
            from={from}
            to={to}
            onFromChange={(v) => {
              setFrom(v);
              setPage(1);
            }}
            onToChange={(v) => {
              setTo(v);
              setPage(1);
            }}
          />
          <div className="w-40">
            <Select value={groupBy ?? 'none'} onValueChange={setRawGroupBy}>
              <SelectTrigger aria-label="Group by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {groupByOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.value === 'none' ? 'No grouping' : `By ${o.label.toLowerCase()}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {workItem ? (
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Clear work item filter ${workItem.label}`}
              onClick={() => {
                setWorkItem(null);
                setPage(1);
              }}
            >
              <span className="max-w-48 truncate">{workItem.label}</span>
              <X className="size-3.5" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-muted-foreground text-sm">
            Total:{' '}
            <span className="text-foreground font-medium">
              {summary ? formatMinutes(summary.total_minutes) : '—'}
            </span>
          </span>
          <Button variant="outline" onClick={() => void exportCsv(WORKLOGS_PATH, filterParams)}>
            <Sheet className="size-4" /> CSV
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              void downloadFile(`${WORKLOGS_PATH}export-pdf/`, filterParams, 'worklogs.pdf')
            }
          >
            <FileText className="size-4" /> PDF
          </Button>
        </div>
      </div>

      {groups.length > 0 ? (
        <Table aria-label="Worklog groups">
          <TableHeader>
            <TableRow>
              <TableHead>{GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label}</TableHead>
              <TableHead className="text-right">Entries</TableHead>
              <TableHead className="text-right">Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((g) => (
              <TableRow key={String(g.key)}>
                <TableCell>
                  {canFilterBy(g) ? (
                    <button
                      type="button"
                      className="hover:text-primary text-left underline-offset-2 hover:underline"
                      onClick={() => filterToGroup(g)}
                    >
                      {g.label}
                    </button>
                  ) : (
                    <span className="text-muted-foreground">{g.label}</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{g.entries}</TableCell>
                <TableCell className="text-right tabular-nums">{formatMinutes(g.minutes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : isError ? (
        <div className="border-destructive bg-destructive/5 rounded-md border p-4">
          <p className="text-destructive text-sm">{error?.message}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed p-10 text-center">
          <p className="font-medium">No worklogs</p>
          <p className="text-muted-foreground text-sm">
            Log time manually, or start a timer on a work item, to see timesheets here.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Work item</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w.id}>
                <TableCell className="whitespace-nowrap">{w.date}</TableCell>
                <TableCell>
                  {w.user_name ? (
                    <span className="flex items-center gap-2">
                      <PersonAvatar name={w.user_name} photo={w.user_photo} />
                      {w.user_name}
                    </span>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{w.project_name ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{w.work_item_title ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums" title={`${w.hours}h`}>
                  {formatMinutes(w.minutes)}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-xs truncate">{w.description}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={manualEnabled ? 'Edit entry' : 'Edit note'}
                    onClick={() => {
                      setEditing(w);
                      setDialogOpen(true);
                    }}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  {/* Manual logging off: logged time can't be removed by hand. */}
                  {manualEnabled ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Delete entry"
                      onClick={() => setToDelete(w)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'entry' : 'entries'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <TimeEntryFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={editing}
        onSubmit={handleSubmit}
        canLogForOthers={canLogForOthers}
        noteOnly={!manualEnabled}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete worklog?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `${formatMinutes(toDelete.minutes)} on ${toDelete.date} will be removed.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate({ id: toDelete.id, projectId: toDelete.project ?? undefined });
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
